import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "@/components/Reveal";

const MACHINES = [
  {
    id: "one",
    name: "SnackPod™ One",
    kind: "Snack & drink combo",
    cat: "snack",
    img: "https://images.unsplash.com/photo-1625650484478-113df4bfc370?crop=entropy&cs=srgb&fm=jpg&q=85",
    specs: ["600+ item capacity", "IR guaranteed drop", "LED-lit display"],
  },
  {
    id: "chill",
    name: "SnackPod™ Chill",
    kind: "Fresh food chiller",
    cat: "fresh",
    img: "https://images.unsplash.com/photo-1575224526797-5730d09d781d?crop=entropy&cs=srgb&fm=jpg&q=85",
    specs: ["2–5°C refrigeration", "Salads & wraps", "Smart lockers"],
  },
  {
    id: "brew",
    name: "SnackPod™ Brew",
    kind: "Barista coffee bar",
    cat: "coffee",
    img: "https://images.pexels.com/photos/11406429/pexels-photo-11406429.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    specs: ["Bean-to-cup espresso", "20+ café drinks", "Fresh milk system"],
  },
  {
    id: "go",
    name: "SnackPod™ Go",
    kind: "Smart touch cooler",
    cat: "smart",
    img: "https://images.pexels.com/photos/32280745/pexels-photo-32280745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    specs: ["Tap to unlock", "Vision AI billing", "24/7 grab & go"],
  },
];

const FILTERS = [
  { id: "all", label: "All equipment" },
  { id: "snack", label: "Snack & drink" },
  { id: "fresh", label: "Fresh food" },
  { id: "coffee", label: "Coffee" },
  { id: "smart", label: "Smart cooler" },
];

const onSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export default function Showcase() {
  const [filter, setFilter] = useState("all");
  const shown = MACHINES.filter((m) => filter === "all" || m.cat === filter);

  return (
    <section
      id="machines"
      data-testid="machine-showcase-section"
      className="relative py-24 lg:py-32"
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#1B365D]/10 blur-[140px]"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Eyebrow>The fleet</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-[#16120E] sm:text-4xl lg:text-5xl">
              Meet the SnackPod™ lineup.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`machine-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-4 py-2 text-[13px] font-semibold transition-all duration-300 ${
                    filter === f.id
                      ? "border-[#1B365D] bg-[#1B365D] text-[#FBF7EF] shadow-[0_8px_26px_rgba(27,54,93,0.3)]"
                      : "border-black/10 text-stone-600 hover:border-[#1B365D]/40 hover:text-[#1B365D]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {shown.map((m, i) => (
              <motion.article
                layout
                key={m.id}
                data-testid={`machine-card-${m.id}`}
                initial={{ opacity: 0, scale: 0.92, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 24 }}
                transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                onMouseMove={onSpot}
                className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white"
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={m.img}
                    alt={`${m.name} — ${m.kind}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        "radial-gradient(300px circle at var(--mx, 50%) var(--my, 50%), rgba(27,54,93,0.12), transparent 65%)",
                    }}
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-32"
                    style={{
                      background: "linear-gradient(to top, #FFFFFF, transparent)",
                    }}
                  />
                </div>
                <div className="relative -mt-10 p-5">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A8861D]">
                    {m.kind}
                  </p>
                  <h3 className="mt-1.5 font-display text-xl font-bold tracking-tight text-[#16120E]">
                    {m.name}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {m.specs.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-black/10 bg-black/[0.03] px-2.5 py-1 font-mono text-[10px] tracking-wide text-stone-600"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.1} className="mt-10">
          <p className="text-center font-mono text-xs uppercase tracking-[0.22em] text-slate-500">
            Every unit ships free · installed by certified technicians · temperature-calibrated
          </p>
        </Reveal>
      </div>
    </section>
  );
}
